import type { SaveRecord } from "@highmarch/app";
import type { GearItem, HubSnapshot, LevelSim, Order } from "@highmarch/domain";
import { BattleTally, hubProgress, hubSnapshot, runStars } from "@highmarch/domain";
import { systemClock } from "../infra/adapters";
import { reloadWhenSaved } from "../infra/save/openSaveStore";
import { toast } from "../ui/toast";
import { features } from "../features/registry";
import { orderTitle } from "../ui/hub/ordersSheet";
import type { SettingsSheetActions } from "../ui/settings";
import { GUIDE_SAVE_VERSION, GuideSaveJson, GuideScreen, guide, guideOffByUrl, setGuide } from "../ui/guide/guide";
import { createGuide } from "../ui/guide/guideController";
import { hubScript } from "../ui/guide/scripts/hub";
import { level1Script } from "../ui/guide/scripts/level1";
import { levelHintsScript } from "../ui/guide/scripts/levelHints";
import { endScript } from "../ui/guide/scripts/end";
import { PRACTICE_LEVEL, guideUnlocks } from "../ui/guide/topics";
import { showGuideSheet } from "../ui/guide/guideSheet";
import type { GameServices, Nav, Screens } from "./context";
import { WyrmBank } from "./wyrmBank";

/** Captain Bram's four scripts (ui/guide/scripts), registered in this order: registration order breaks priority ties. */
const GUIDE_SCRIPTS = [hubScript, level1Script, levelHintsScript, endScript];
const GUIDE_SCREENS: readonly GuideScreen[] = ["title", "hub", "level", "end"];
/** The retired coach's "done" index (ui/hub/coach.ts COACH_STEPS): the legacy mirror writes it once hub.play2 is done. */
const LEGACY_COACH_DONE = 4;

/**
 * For one release the guide's progress is mirrored into the legacy fields (DESIGN §9), so a rollback build does not
 * teach anyone again: the level tutorial ends with l1.start (or the skip), the coach with hub.play2, the gate with
 * hub.gearGate. Only ever moves them forward.
 */
function mirrorLegacyGuide(save: SaveRecord, g: GuideSaveJson): void {
  const done = new Set(g.done);
  if (done.has("l1.start") || g.walkthroughSkipped) save.tutorialStep = -1;
  if (done.has("hub.play2")) save.coachStep = LEGACY_COACH_DONE;
  if (done.has("hub.gearGate")) save.gearGateSeen = true;
}

/**
 * Part of the composition root: the save (persist, reset), Daily Orders' bookkeeping, Captain Bram's install and his
 * Guide sheet, and what the Settings sheet is given outside a level.
 */
export class MetaFlow {
  /**
   * Daily Orders (meta/orders.ts). The hub orders are read from the profile between two saves (every hub action saves),
   * the battle orders from a tally of the level's sim events, fed each tick just before the sim clears them.
   */
  private hubSnap: HubSnapshot;
  private tally: BattleTally | null = null;
  /** Orders a battle or a hub action completed, named in a toast on the next hub. */
  private ordersDone: Order[] = [];

  /** M40: the Starfall Wyrm's reward (banked the tick it falls) and its paid retry (boot/wyrmBank.ts). */
  readonly wyrm: WyrmBank;

  constructor(private readonly g: GameServices, private readonly screens: Screens, private readonly nav: Nav) {
    this.hubSnap = hubSnapshot(g.catalog.profile);
    this.wyrm = new WyrmBank(g.catalog, () => this.persist());
  }

  persist(): void {
    this.trackHubOrders();
    this.g.catalog.applyTo(this.g.save);
    this.g.saves.save(this.g.save);
    features.persisted();   // seam: features that react to every save (features/rewardFly.ts)
  }

  /** Settings → reset: wipes the local save and reloads into a fresh profile once the wipe is on disk. */
  resetSave(): Promise<void> {
    this.g.saves.deleteAll();
    return reloadWhenSaved();
  }

  /** Settings from the title or the hub: the sheet plus the save backup and the Guide row (never offered in a level). */
  settingsActions(): SettingsSheetActions {
    return { resetSave: () => this.resetSave(), playerId: this.g.save.playerId, backup: this.g.keeper.backup(), onGuide: () => this.nav.openGuide() };
  }

  private trackHubOrders(): void {
    const c = this.g.catalog;
    this.ordersDone.push(...c.orders.record(hubProgress(this.hubSnap, c.profile)));
    this.hubSnap = hubSnapshot(c.profile);
    if (this.screens.hub) this.announceOrders();
  }

  /** A level's sim starts: its events are tallied for the battle orders each tick, just before the sim clears them. */
  watchBattle(sim: LevelSim): void {
    const tally = new BattleTally();
    this.tally = tally;
    this.rareGear = [];
    this.wyrm.reset();
    sim.events.beforeClear = () => { tally.observe(sim); this.bankRareChest(sim); this.wyrm.now(sim); };
    // MP-21: the end screen's event block reads the tally so far (an event's kills, elites, early calls) before the level is left.
    features.battleCounts = () => (this.tally === tally ? tally.peek(sim, this.screens.level?.session.commands ?? []) : {});
  }

  /**
   * M36 (docs/RARES.md): the gear of the rare's chest this battle banked (empty until a rare dies), for the mid-level
   * chest reveal. The chest is banked the moment `sim.rareChest` appears (RareKilled), once per chest
   * (LevelCatalog.bankRareChest keys it), and the save written, so a later loss or a closed page keeps it.
   */
  rareGear: GearItem[] = [];
  /** The rare chest's gear, banking it first if this tick's chest has not been banked yet (paused on a rally offer). */
  rareGearNow(sim: LevelSim): GearItem[] { this.bankRareChest(sim); return this.rareGear; }
  private bankRareChest(sim: LevelSim): void {
    if (sim.rareChest === null) return;
    const gear = this.g.catalog.bankRareChest(sim.rareChest);
    if (gear === null) return;
    this.rareGear = gear;
    this.persist();
  }

  recordBattleOrders(): void {
    const lv = this.screens.level, tally = this.tally;
    this.tally = null;
    if (!lv || !tally) return;
    tally.observe(lv.sim);                                    // the last tick's events: nothing clears them any more
    this.bankRareChest(lv.sim);                               // M36: a rare killed on the last tick (once per chest)
    this.wyrm.now(lv.sim);                                    // M40: a Wyrm slain on the last tick
    lv.sim.events.beforeClear = null;
    this.g.catalog.orders.refresh(systemClock.utcNowSeconds(), this.g.catalog.highestLevelCleared);
    const counts = tally.finish(lv.sim, lv.session.commands);
    this.ordersDone.push(...this.g.catalog.orders.record(counts));
    features.battleEnded({ sim: lv.sim, counts, damageByTower: lv.session.damageByTower, assistsByTower: lv.session.assistsByTower, stars: runStars(lv.sim, lv.session.tally) });   // WP7: lifetime stats; MP-27: stars for battleWon
  }

  /** "Order done" toasts wait while Captain Bram is busy (his first session); the hub's tick tries again. */
  announceOrders(): void {
    if (this.ordersDone.length === 0 || guide()?.quiet() === false) return;
    const names = this.ordersDone.map((o) => orderTitle(this.g.catalog, o));
    this.ordersDone = [];
    toast(this.g.ui, `Order done: ${names.join(" · ")}. Claim it in Events`);
    this.screens.hub?.rerender();
  }

  /**
   * Captain Bram (ui/guide): one controller for the welcome, the level-1 walkthrough, the hints and the hub's first
   * session, with its progress in the save. `?noguide` leaves it out: every screen then runs with no tutorial at all
   * (the smoke test and captures).
   */
  installGuide(): void {
    if (guideOffByUrl(location.search)) return;
    const save = this.g.save;
    const g = createGuide({
      saved: save.guide, legacy: save, unlocks: guideUnlocks(this.g.balance),
      persist: (s) => { save.guide = s; mirrorLegacyGuide(save, s); this.g.saves.save(save); },
      now: () => performance.now(),
    });
    for (const script of GUIDE_SCRIPTS) g.register(script);
    setGuide(g);
  }

  /** Debug "Reset guide": the captain starts over (the whole first session again on a fresh profile). */
  resetGuide(): void {
    const old = guide();
    if (!old) return;
    for (const screen of GUIDE_SCREENS) old.detach(screen);
    const save = this.g.save;
    save.guide = { v: GUIDE_SAVE_VERSION, done: [] };
    save.tutorialStep = 0;
    save.coachStep = undefined;
    save.gearGateSeen = undefined;
    this.g.saves.save(save);
    this.installGuide();
    this.screens.hub?.rerender();                              // attaches the hub to the new controller
  }

  /** More → Guide, or Settings → Guide: from the title the practice battle leaves the title first. */
  openGuide(): void {
    showGuideSheet(this.g.ui, {
      highestCleared: this.g.catalog.highestLevelCleared, unlocks: guideUnlocks(this.g.balance),
      onPractice: () => {
        guide()?.startPractice();
        this.screens.title?.dispose(); this.screens.title = null;
        this.nav.requestStart(PRACTICE_LEVEL);
      },
    });
  }
}
